import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { IOSDevice } from './components/IOSDevice';
import { PhoneLoginScreen } from './components/screens/PhoneLoginScreen';
import { PhoneRegisterScreen } from './components/screens/PhoneRegisterScreen';
import { ProjectsScreen } from './components/screens/ProjectsScreen';
import { WelcomeScreen } from './components/screens/WelcomeScreen';
import { EventScreen } from './components/screens/EventScreen';
import { UploadScreen } from './components/screens/UploadScreen';
import { StyleScreen } from './components/screens/StyleScreen';
import { MusicScreen } from './components/screens/MusicScreen';
import { ProcessingScreen } from './components/screens/ProcessingScreen';
import { PreviewScreen } from './components/screens/PreviewScreen';
import { PricingScreen } from './components/screens/PricingScreen';
import { SuccessScreen } from './components/screens/SuccessScreen';
import { api } from './lib/api';
import { formatCallableError } from './lib/callableError';
import { getAuthOrThrow } from './lib/firebase';
import { useJob } from './hooks/useJob';
import { useMyJobs } from './hooks/useMyJobs';
import type {
  EventType,
  JobDoc,
  MusicId,
  PackageId,
  PhotoRef,
  StyleId,
} from './lib/types';

type Step =
  | 'projects'
  | 'welcome'
  | 'event'
  | 'upload'
  | 'style'
  | 'music'
  | 'processing'
  | 'preview'
  | 'pricing'
  | 'success';

const PIP_STEPS: Step[] = ['event', 'upload', 'style', 'music', 'pricing'];

const STEP_LABELS: Record<Step, string> = {
  projects: '00 Library',
  welcome: '01 Welcome',
  event: '02 Event',
  upload: '03 Upload',
  style: '04 Style',
  music: '05 Music',
  processing: '06 Processing',
  preview: '07 Preview',
  pricing: '08 Pricing',
  success: '09 Success',
};

const LIB_LOG = '[moments:library]';

export default function App() {
  const [authReady, setAuthReady] = useState(false);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [phoneAuthMode, setPhoneAuthMode] = useState<'login' | 'register'>('register');
  const [step, setStep] = useState<Step>('welcome');
  const [event, setEvent] = useState<EventType | null>(null);
  const [photos, setPhotos] = useState<PhotoRef[]>([]);
  const [styleSel, setStyleSel] = useState<StyleId | null>(null);
  const [music, setMusic] = useState<MusicId | null>(null);
  const [pkg, setPkg] = useState<PackageId>('plus');
  const [jobId, setJobId] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  /** When true, “back” from early wizard steps returns to the library instead of welcome. */
  const [resumeFromProjects, setResumeFromProjects] = useState(false);
  const job = useJob(jobId);
  const startedRef = useRef(false);
  const { jobs: myJobs, loading: myJobsLoading } = useMyJobs();
  const libraryRouteOnce = useRef(false);
  const libraryLoadingLogged = useRef(false);
  const [libraryResolved, setLibraryResolved] = useState(false);

  const signedIn = Boolean(authUser);

  useEffect(() => {
    const auth = getAuthOrThrow();
    return onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthReady(true);
    });
  }, []);

  // Handle Meshulam redirect (success / cancel) after sign-in so URL is not lost.
  useEffect(() => {
    if (!signedIn) return;
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('paymentResult');
    const returnedJob = params.get('jobId');
    if (paymentResult && returnedJob) {
      setJobId(returnedJob);
      if (paymentResult === 'success') {
        setResumeFromProjects(true);
        setStep('success');
      } else {
        setResumeFromProjects(true);
        setStep('pricing');
      }
      // Clean the URL so refreshes don't re-trigger.
      const clean = new URL(window.location.href);
      clean.search = '';
      window.history.replaceState({}, '', clean.toString());
    }
  }, [signedIn]);

  // After first jobs snapshot: route to library when there are jobs; avoid welcome flash.
  useEffect(() => {
    if (!signedIn) return;

    if (myJobsLoading) {
      if (!libraryLoadingLogged.current) {
        libraryLoadingLogged.current = true;
        console.log(
          `${LIB_LOG} App: waiting for project list (useMyJobs loading — auth + Firestore snapshot)`,
        );
      }
      return;
    }
    libraryLoadingLogged.current = false;

    if (!libraryRouteOnce.current) {
      libraryRouteOnce.current = true;
      if (myJobs.length > 0) {
        console.log(
          `${LIB_LOG} App: first routing decision → **projects** (הסרטים שלי + “סרט חדש” +)`,
          { projectCount: myJobs.length, jobIds: myJobs.map((j) => j.id) },
        );
        setStep('projects');
        setResumeFromProjects(true);
      } else {
        console.log(
          `${LIB_LOG} App: first routing decision → **onboarding** (welcome — no projects yet)`,
          { projectCount: 0 },
        );
      }
    }
    setLibraryResolved(true);
  }, [signedIn, myJobsLoading, myJobs.length]);

  const go = useCallback((s: Step) => setStep(s), []);

  const hydrateJobState = useCallback((id: string, j: JobDoc) => {
    setJobId(id);
    setEvent(j.eventType ?? 'other');
    setPhotos(j.photos ?? []);
    setStyleSel(j.styleId ?? null);
    setMusic(j.musicId ?? null);
    setPkg(j.pkg ?? 'plus');
  }, []);

  /** Same entry as Welcome → «יוצרים סרטון ראשון»: skip welcome, open event picker. */
  const startNewMovie = useCallback(() => {
    setResumeFromProjects(true);
    setJobId(null);
    setEvent(null);
    setPhotos([]);
    setStyleSel(null);
    setMusic(null);
    setPkg('plus');
    setStep('event');
  }, []);

  const goToEvent = useCallback(async () => {
    setResumeFromProjects(false);
    setStep('event');
  }, []);

  // Lazily create the job row when leaving the event screen.
  const goToUpload = useCallback(async () => {
    if (!event) return;
    if (!jobId) {
      try {
        const res = await api.createMomentJob({ eventType: event });
        setJobId(res.jobId);
      } catch (e) {
        setBootError(e instanceof Error ? e.message : String(e));
        return;
      }
    } else {
      // Update event type if user changed it on the way back.
      try {
        await api.setJobChoices({ jobId });
      } catch {
        /* noop */
      }
    }
    setStep('upload');
  }, [event, jobId]);

  const goToStyle = useCallback(async () => {
    if (!jobId) return;
    await api.setJobChoices({ jobId, photos }).catch(() => undefined);
    setStep('style');
  }, [jobId, photos]);

  const goToMusic = useCallback(async () => {
    if (!jobId || !styleSel) return;
    await api.setJobChoices({ jobId, styleId: styleSel }).catch(() => undefined);
    setStep('music');
  }, [jobId, styleSel]);

  const goToProcessing = useCallback(async () => {
    if (!jobId || !music) return;
    try {
      setBootError(null);
      await api.setJobChoices({ jobId, musicId: music });
      await api.startGeneration({ jobId });
      setStep('processing');
    } catch (e) {
      setBootError(formatCallableError(e));
    }
  }, [jobId, music]);

  const openJobPreview = useCallback(
    (id: string, j: JobDoc) => {
      hydrateJobState(id, j);
      setResumeFromProjects(true);
      setStep('preview');
    },
    [hydrateJobState],
  );

  const openJobPurchase = useCallback(
    (id: string, j: JobDoc) => {
      hydrateJobState(id, j);
      setResumeFromProjects(true);
      setStep('pricing');
    },
    [hydrateJobState],
  );

  const openJobSuccess = useCallback(
    (id: string, j: JobDoc) => {
      hydrateJobState(id, j);
      setResumeFromProjects(true);
      setStep('success');
    },
    [hydrateJobState],
  );

  const continueDraft = useCallback(
    (id: string, j: JobDoc) => {
      hydrateJobState(id, j);
      setResumeFromProjects(true);
      const ph = j.photos ?? [];
      if (ph.length === 0) {
        setStep('event');
        return;
      }
      if (!j.styleId) {
        setStep('upload');
        return;
      }
      if (!j.musicId) {
        setStep('style');
        return;
      }
      setStep('music');
    },
    [hydrateJobState],
  );

  const continueProcessing = useCallback(
    (id: string, j: JobDoc) => {
      hydrateJobState(id, j);
      setResumeFromProjects(true);
      setStep('processing');
    },
    [hydrateJobState],
  );

  const backToProjectsFromUpload = useCallback(() => {
    setResumeFromProjects(true);
    go('projects');
  }, [go]);

  const removeProject = useCallback(
    async (id: string) => {
      setBootError(null);
      try {
        await api.deleteMomentJob({ jobId: id });
        if (jobId === id) {
          setJobId(null);
          setEvent(null);
          setPhotos([]);
          setStyleSel(null);
          setMusic(null);
          setPkg('plus');
          if (step !== 'projects' && step !== 'welcome') {
            setResumeFromProjects(true);
            setStep('projects');
          }
        }
      } catch (e) {
        setBootError(formatCallableError(e));
      }
    },
    [jobId, step],
  );

  // When the job finishes rendering, slide forward to preview.
  useEffect(() => {
    if (step !== 'processing') return;
    if (!job) return;
    if (job.status === 'ready' || job.status === 'paid') {
      setStep('preview');
    }
  }, [step, job?.status]);

  // When a payment is confirmed by webhook, jump to success.
  useEffect(() => {
    if (!job) return;
    if (job.status === 'paid' && step !== 'success') {
      setStep('success');
    }
  }, [job?.status, step]);

  const screen = useMemo(() => {
    switch (step) {
      case 'projects':
        return (
          <ProjectsScreen
            jobs={myJobs}
            loading={myJobsLoading}
            onOpenPreview={openJobPreview}
            onOpenPurchase={openJobPurchase}
            onOpenSuccess={openJobSuccess}
            onContinueDraft={continueDraft}
            onContinueProcessing={continueProcessing}
            onRemoveProject={removeProject}
            onNewMovie={startNewMovie}
          />
        );
      case 'welcome':
        return <WelcomeScreen onNext={goToEvent} />;
      case 'event':
        return (
          <EventScreen
            value={event}
            onChange={setEvent}
            onNext={goToUpload}
            onBack={() => go(resumeFromProjects ? 'projects' : 'welcome')}
          />
        );
      case 'upload':
        return (
          <UploadScreen
            jobId={jobId}
            photos={photos}
            onPhotosUploaded={async (next) => {
              setPhotos(next);
              if (jobId) {
                await api.setJobChoices({ jobId, photos: next }).catch(() => undefined);
              }
            }}
            onNext={goToStyle}
            onBack={() => go('event')}
            onBackToProjects={jobId ? backToProjectsFromUpload : undefined}
          />
        );
      case 'style':
        return (
          <StyleScreen
            value={styleSel}
            onChange={setStyleSel}
            onNext={goToMusic}
            onBack={() => go('upload')}
          />
        );
      case 'music':
        return (
          <MusicScreen
            value={music}
            onChange={setMusic}
            onNext={goToProcessing}
            onBack={() => go('style')}
          />
        );
      case 'processing':
        return <ProcessingScreen job={job} />;
      case 'preview':
        return (
          <PreviewScreen
            job={job}
            onNext={() => go('pricing')}
            onBack={() => go(resumeFromProjects ? 'projects' : 'music')}
          />
        );
      case 'pricing':
        return (
          <PricingScreen
            jobId={jobId}
            value={pkg}
            onChange={setPkg}
            onCancel={() => go(resumeFromProjects ? 'projects' : 'preview')}
            onBack={() => go(resumeFromProjects ? 'projects' : 'preview')}
          />
        );
      case 'success':
        return (
          <SuccessScreen
            jobId={jobId}
            job={job}
            onRestart={() => {
              startedRef.current = false;
              setEvent(null);
              setPhotos([]);
              setStyleSel(null);
              setMusic(null);
              setJobId(null);
              setPkg('plus');
              setResumeFromProjects(true);
              setStep('projects');
            }}
          />
        );
    }
  }, [
    step,
    event,
    jobId,
    photos,
    styleSel,
    music,
    pkg,
    job,
    go,
    goToEvent,
    goToUpload,
    goToStyle,
    goToMusic,
    goToProcessing,
    myJobs,
    myJobsLoading,
    resumeFromProjects,
    openJobPreview,
    openJobPurchase,
    openJobSuccess,
    continueDraft,
    continueProcessing,
    removeProject,
    backToProjectsFromUpload,
    startNewMovie,
  ]);

  let pipIdx = PIP_STEPS.indexOf(step as (typeof PIP_STEPS)[number]);
  if (step === 'processing' || step === 'preview') pipIdx = 4;
  if (step === 'success') pipIdx = 5;

  if (!authReady) {
    return (
      <div className="app-shell" data-screen-label="loading">
        <header className="brand-bar">
          <div className="brand-mark">ר</div>
          <div>
            <div className="brand-name">רגעים</div>
          </div>
          <div className="brand-tag">— הזיכרונות שלכם, סרטון</div>
        </header>
        <p className="lede" style={{ textAlign: 'center', marginTop: 48 }}>
          טוענים…
        </p>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="app-shell" data-screen-label={phoneAuthMode === 'login' ? 'Phone login' : 'Phone register'}>
        <header className="brand-bar">
          <div className="brand-mark">ר</div>
          <div>
            <div className="brand-name">רגעים</div>
          </div>
          <div className="brand-tag">— הזיכרונות שלכם, סרטון</div>
        </header>

        <div className="device-stage">
          <IOSDevice>
            {phoneAuthMode === 'login' ? (
              <PhoneLoginScreen
                onGoRegister={() => setPhoneAuthMode('register')}
                onSuccess={() => undefined}
              />
            ) : (
              <PhoneRegisterScreen
                onGoLogin={() => setPhoneAuthMode('login')}
                onSuccess={() => undefined}
              />
            )}
          </IOSDevice>
        </div>
      </div>
    );
  }

  if (!libraryResolved) {
    return (
      <div className="app-shell" data-screen-label="loading">
        <header className="brand-bar">
          <div className="brand-mark">ר</div>
          <div>
            <div className="brand-name">רגעים</div>
          </div>
          <div className="brand-tag">— הזיכרונות שלכם, סרטון</div>
        </header>
        <p className="lede" style={{ textAlign: 'center', marginTop: 48 }}>
          טוענים…
        </p>
      </div>
    );
  }

  return (
    <div className="app-shell" data-screen-label={STEP_LABELS[step]}>
      <header className="brand-bar">
        <div className="brand-mark">ר</div>
        <div>
          <div className="brand-name">רגעים</div>
        </div>
        <div className="brand-tag">— הזיכרונות שלכם, סרטון</div>
      </header>

      <div className="device-stage">
        <IOSDevice>{screen}</IOSDevice>
      </div>

      {step !== 'welcome' && step !== 'success' && step !== 'projects' && (
        <div className="step-pips">
          {PIP_STEPS.map((_, i) => (
            <div
              key={i}
              className={'pip ' + (i === pipIdx ? 'active' : i < pipIdx ? 'done' : '')}
            />
          ))}
        </div>
      )}

      {bootError && (
        <div
          style={{
            margin: '8px auto 0',
            maxWidth: 480,
            background: 'oklch(0.92 0.05 30 / 0.6)',
            color: 'var(--ink)',
            padding: '8px 12px',
            borderRadius: 12,
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          {bootError}
        </div>
      )}
    </div>
  );
}
